import { useCallback, useMemo, useRef, useState } from 'react';
import { useKanbanLeads } from '../hooks/useKanbanLeads';
import { LeadCard } from './LeadCard.jsx';
import { EmptyState } from './EmptyState.jsx';

const STORAGE_KEY = (userId) => `crm:substatus-filter:${userId}`;

function getHiddenForColumn(userId, parentStatusId) {
  try {
    const raw = localStorage.getItem(STORAGE_KEY(userId));
    if (!raw) return new Set();
    const data = JSON.parse(raw);
    return new Set(data[parentStatusId] || []);
  } catch { return new Set(); }
}

function setHiddenForColumn(userId, parentStatusId, hiddenArray) {
  try {
    const raw = localStorage.getItem(STORAGE_KEY(userId));
    const data = raw ? JSON.parse(raw) : {};
    data[parentStatusId] = hiddenArray;
    localStorage.setItem(STORAGE_KEY(userId), JSON.stringify(data));
  } catch { /* ignore */ }
}

export const KanbanColumn = ({
  status,
  isAdmin,
  selectedLeadId,
  draggedLeadId,
  dragTargetStatus,
  busyLeadId,
  searchQuery,
  managerFilter,
  onSelectLead,
  onCardDragStart,
  onCardDragEnd,
  onDeleteLead,
  onColumnDragOver,
  onColumnDrop,
  onColumnDragLeave,
  onOpenCreateModal,
  unreadLeadIds = [],
  unreadCounts = {},
  showUnreadOnly = false,
  statusLookup,
  userId,
  allStatuses = [],
  lastContactDays,
}) => {
  const [filterOpen, setFilterOpen] = useState(false);
  const [hiddenSubs, setHiddenSubs] = useState(() => getHiddenForColumn(userId, status.id));

  const childStatuses = useMemo(
    () => allStatuses.filter(s => s.parentId === status.id),
    [allStatuses, status.id]
  );

  const statusIn = useMemo(() => {
    if (hiddenSubs.size === 0 || childStatuses.length === 0) return undefined;
    const visible = childStatuses.filter(c => !hiddenSubs.has(c.id)).map(c => c.id);
    return visible.length > 0 && visible.length < childStatuses.length
      ? visible.join(',')
      : undefined;
  }, [hiddenSubs, childStatuses]);

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
  } = useKanbanLeads(status.id, { search: searchQuery, managerFilter, statusIn, lastContactDays });

  const allLeads = data?.pages?.flatMap(p => p.leads) ?? [];
  const leads = showUnreadOnly
    ? allLeads.filter(lead => unreadLeadIds.includes(lead.id))
    : allLeads;
  const total = data?.pages?.[0]?.pagination?.total ?? 0;

  const bodyRef = useRef(null);

  const handleScroll = useCallback(() => {
    if (!bodyRef.current || isFetchingNextPage || !hasNextPage) return;
    const { scrollTop, scrollHeight, clientHeight } = bodyRef.current;
    if (scrollHeight - scrollTop - clientHeight < 150) {
      fetchNextPage();
    }
  }, [isFetchingNextPage, hasNextPage, fetchNextPage]);

  // Browsers fire dragenter and dragleave for every child element the pointer crosses, so
  // the column counts them and reacts only to the first enter and the last leave.
  const dragCounter = useRef(0);

  const handleDragEnter = useCallback((event) => {
    event.preventDefault();
    dragCounter.current += 1;
    if (dragCounter.current === 1) {
      onColumnDragOver(status.id);
    }
  }, [onColumnDragOver, status.id]);

  const handleDragOver = useCallback((event) => {
    event.preventDefault();
  }, []);

  const handleDragLeave = useCallback(() => {
    dragCounter.current -= 1;
    if (dragCounter.current === 0) {
      onColumnDragLeave(status.id);
    }
  }, [onColumnDragLeave, status.id]);

  const handleDrop = useCallback((event) => {
    event.preventDefault();
    dragCounter.current = 0;
    onColumnDrop(status.id);
  }, [onColumnDrop, status.id]);

  return (
    <section
      className={[
        'kanban-column',
        dragTargetStatus === status.id ? 'kanban-column--active-drop' : '',
        status.id === 'manager_response_needed' ? 'kanban-column--attention' : ''
      ].filter(Boolean).join(' ')}
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <header className='kanban-column__header' style={{ position: 'relative' }}>
        <div>
          <h2>{{
            'Ожидают аутрича': 'Ожидают',
            'Активный амбассадор': 'Амбассадоры',
            'Брак': 'Отказы',
          }[status.label] || status.label}</h2>
          <span>{total} шт.</span>
        </div>
        <div className='kanban-column__header-actions'>
          {childStatuses.length > 0 && (
            <button
              type='button'
              className={`kanban-column__filter-toggle${filterOpen ? ' kanban-column__filter-toggle--open' : ''}${hiddenSubs.size > 0 ? ' kanban-column__filter-toggle--filtered' : ''}`}
              onClick={() => setFilterOpen(prev => !prev)}
              title='Фильтр подстатусов'
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.5"/>
                <path d="M5 8l2 2 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          )}
        </div>
        {filterOpen && childStatuses.length > 0 && (
          <div className='substatus-filter'>
            {childStatuses.map(child => {
              const isHidden = hiddenSubs.has(child.id);
              return (
                <label key={child.id} className='substatus-filter__item'>
                  <input
                    type='checkbox'
                    checked={!isHidden}
                    onChange={() => {
                      const next = new Set(hiddenSubs);
                      if (isHidden) next.delete(child.id);
                      else next.add(child.id);
                      setHiddenSubs(next);
                      setHiddenForColumn(userId, status.id, [...next]);
                    }}
                  />
                  <span>{child.label}</span>
                </label>
              );
            })}
          </div>
        )}
      </header>

      <div
        className='kanban-column__body'
        ref={bodyRef}
        onScroll={handleScroll}
      >
        {isLoading ? (
          <div className='kanban-column__loader'>
            <div className='spinner' />
          </div>
        ) : leads.length === 0 ? (
          <EmptyState
            title='Пока пусто'
            description='Добавьте лид или измените фильтры'
          />
        ) : (
          <>
            {leads.map(lead => (
              <LeadCard
                key={lead.id}
                lead={lead}
                status={status}
                isActive={selectedLeadId === lead.id}
                isDragging={draggedLeadId === lead.id}
                isBusy={busyLeadId === lead.id}
                showOwner={isAdmin}
                onSelect={onSelectLead}
                onDragStart={onCardDragStart}
                onDragEnd={onCardDragEnd}
                unreadCount={unreadCounts[lead.id]}
                statusLookup={statusLookup}
              />
            ))}
            {isFetchingNextPage && (
              <div className='kanban-column__loading-more'>
                <div className='spinner spinner--small' />
                <span>Загрузка...</span>
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
};

export default KanbanColumn;
